import { useEffect, useState } from 'react';
import { FALLBACK_MODES, type EmbedSnippetResponse, type TenantSettings } from '@stonelink/shared';
import { api } from '../api';
import { Button, Card, ErrorNote, Input, Label, PageHeader, Textarea } from '../ui';

const FALLBACK_LABEL: Record<(typeof FALLBACK_MODES)[number], string> = {
  message: 'Offer to take a message',
  contact: 'Share our contact details',
  both: 'Both — take a message and share contact details',
};

/** Branding, persona, fallback behaviour, live preview and the embed snippet. */
export function AppearancePage() {
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [embed, setEmbed] = useState<EmbedSnippetResponse | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [loadedSettings, loadedEmbed] = await Promise.all([
          api.getSettings(),
          api.embedSnippet(),
        ]);
        setSettings(loadedSettings);
        setEmbed(loadedEmbed);
      } catch {
        setError('Could not load your settings.');
      }
    })();
  }, []);

  const update = <K extends keyof TenantSettings>(key: K, value: TenantSettings[K]) => {
    setSettings((prev) => (prev ? { ...prev, [key]: value } : prev));
    setSaved(false);
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    try {
      setSettings(await api.updateSettings(settings));
      setSaved(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save your settings.');
    } finally {
      setSaving(false);
    }
  };

  const copySnippet = async () => {
    if (!embed) return;
    await navigator.clipboard.writeText(embed.snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!settings) {
    return (
      <div>
        <PageHeader
          title="Appearance & Embed"
          description="How your assistant looks and behaves."
        />
        {error ? <ErrorNote>{error}</ErrorNote> : <p className="text-sm text-muted">Loading…</p>}
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Appearance & Embed"
        description="How your assistant looks, how it sounds, and what it does when it cannot help."
      />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-5">
          <Card title="Branding">
            <div className="space-y-4">
              <div>
                <Label htmlFor="botName">Assistant name</Label>
                <Input
                  id="botName"
                  value={settings.botName}
                  maxLength={60}
                  onChange={(event) => update('botName', event.target.value)}
                />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Label htmlFor="brandColor">Brand colour</Label>
                  <Input
                    id="brandColor"
                    value={settings.brandColor}
                    placeholder="#0f766e"
                    onChange={(event) => update('brandColor', event.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="colorPicker">Pick</Label>
                  <input
                    id="colorPicker"
                    type="color"
                    aria-label="Brand colour picker"
                    value={
                      /^#[0-9a-fA-F]{6}$/.test(settings.brandColor)
                        ? settings.brandColor
                        : '#0f766e'
                    }
                    onChange={(event) => update('brandColor', event.target.value)}
                    className="h-11 w-14 cursor-pointer rounded-xl border border-line bg-white/5 p-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="logoUrl">Logo URL (optional)</Label>
                <Input
                  id="logoUrl"
                  value={settings.logoUrl ?? ''}
                  placeholder="https://example.com/logo.png"
                  onChange={(event) => update('logoUrl', event.target.value || null)}
                />
              </div>
            </div>
          </Card>

          <Card title="Voice">
            <div className="space-y-4">
              <div>
                <Label htmlFor="greeting">First message</Label>
                <Textarea
                  id="greeting"
                  rows={2}
                  value={settings.greeting}
                  maxLength={500}
                  onChange={(event) => update('greeting', event.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="personaNotes">Tone instructions</Label>
                <Textarea
                  id="personaNotes"
                  rows={3}
                  value={settings.personaNotes}
                  maxLength={2000}
                  placeholder="Warm and direct. Sound like a member of a small team, not a corporate script."
                  onChange={(event) => update('personaNotes', event.target.value)}
                />
                <p className="mt-1 text-xs text-muted">
                  Visitors never see this — it shapes how the assistant writes.
                </p>
              </div>
            </div>
          </Card>

          <Card
            title="When it cannot help"
            description="Every unanswered question is logged in Gaps regardless of this setting."
          >
            <div className="space-y-4">
              <fieldset>
                <legend className="mb-1.5 text-sm font-medium">Fallback behaviour</legend>
                <div className="space-y-2">
                  {FALLBACK_MODES.map((mode) => (
                    <label key={mode} className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="fallbackMode"
                        value={mode}
                        checked={settings.fallbackMode === mode}
                        onChange={() => update('fallbackMode', mode)}
                      />
                      {FALLBACK_LABEL[mode]}
                    </label>
                  ))}
                </div>
              </fieldset>
              <div>
                <Label htmlFor="contactInfo">Contact details to share</Label>
                <Input
                  id="contactInfo"
                  value={settings.contactInfo ?? ''}
                  placeholder="hello@example.com or 555-0123"
                  onChange={(event) => update('contactInfo', event.target.value || null)}
                />
              </div>
            </div>
          </Card>

          <div className="flex items-center gap-3">
            <Button onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
            {saved && <span className="text-sm text-brand">Saved.</span>}
            {error && <ErrorNote>{error}</ErrorNote>}
          </div>
        </div>

        <div className="space-y-5">
          <Card title="Preview" description="Roughly how the widget will look on your site.">
            <WidgetPreview settings={settings} />
          </Card>

          <Card title="Install" description="Paste this once, anywhere before </body>.">
            <pre className="overflow-x-auto rounded-lg bg-[#14181f] p-3 text-xs leading-relaxed text-[#d7e3e1]">
              <code>{embed?.snippet ?? '…'}</code>
            </pre>
            <div className="mt-3">
              <Button variant="secondary" onClick={() => void copySnippet()} disabled={!embed}>
                {copied ? 'Copied' : 'Copy snippet'}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function WidgetPreview({ settings }: { settings: TenantSettings }) {
  const color = /^#[0-9a-fA-F]{6}$/.test(settings.brandColor) ? settings.brandColor : '#0f766e';
  // Deliberately light: this previews a white-label widget as it will look on
  // the customer's own site, not as part of this console's chrome.
  return (
    <div className="overflow-hidden rounded-xl border border-line bg-white text-[#14181f]">
      <div
        className="flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-white"
        style={{ background: color }}
      >
        {settings.logoUrl && (
          <img src={settings.logoUrl} alt="" className="h-6 w-6 rounded object-cover" />
        )}
        <span className="truncate">{settings.botName || 'Assistant'}</span>
      </div>
      <div className="space-y-2 bg-slate-50 p-3">
        <div className="max-w-[85%] rounded-2xl rounded-bl-md border border-line bg-white px-3 py-2 text-sm">
          {settings.greeting || 'Hi! How can I help?'}
        </div>
        <div
          className="ml-auto max-w-[70%] rounded-2xl rounded-br-md px-3 py-2 text-sm text-white"
          style={{ background: color }}
        >
          What are your hours?
        </div>
      </div>
      <div className="flex items-center gap-2 border-t border-line bg-white p-2.5">
        <div className="h-8 flex-1 rounded-lg border border-line px-2 text-xs leading-8 text-muted">
          Type your message…
        </div>
        <div className="h-8 w-8 rounded-lg" style={{ background: color }} />
      </div>
    </div>
  );
}
